<template>
  <MarketInsightCard
    :title="$t('market_insights.norway_reservoir.title')"
    :description="$t('market_insights.norway_reservoir.description')"
    icon="fa-solid fa-water"
    fullscreenable
    :infoText="$t('market_insights.norway_reservoir.info_text')"
  >
    <template #content>
      <!-- Trading Zone Dropdown -->
      <div class="mb-3">
        <label class="form-label form-label-sm">{{
          $t("market_insights.norway_reservoir.zone_label")
        }}</label>
        <select
          class="form-select form-select-sm"
          v-model="selectedZone"
          :disabled="isLoading"
        >
          <option
            v-for="zone in zoneOptions"
            :key="zone.value"
            :value="zone.value"
            :disabled="zone.disabled"
          >
            {{ zone.disabled ? "🔒 " : "" }}{{ zone.label }}
          </option>
        </select>
        <small v-if="!allZonesUnlocked" class="text-muted mt-1 d-block">
          <font-awesome-icon icon="fa-solid fa-lock" class="me-1" />
          {{ $t("market_insights.norway_reservoir.locked_zones_hint") }}
        </small>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="chart-loading-placeholder">
        <font-awesome-icon icon="fa-solid fa-spinner" spin class="me-2" />
        {{ $t("market_insights.norway_reservoir.loading") }}
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger mb-0">
        <font-awesome-icon
          icon="fa-solid fa-exclamation-triangle"
          class="me-2"
        />
        {{ $t("market_insights.norway_reservoir.error") }}
      </div>

      <!-- Chart -->
      <LineChart
        v-else-if="chartData"
        :chartData="chartData"
        :yAxisTitle="$t('market_insights.norway_reservoir.y_axis_label')"
        :height="350"
        :showLegend="true"
        legendPosition="bottom"
      />
    </template>
  </MarketInsightCard>
</template>

<script setup lang="ts">
import LineChart from "@/common/LineChart.vue";
import { PALETTE } from "@/composables/chartPalette";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { RESERVOIR_LEVELS_URLS } from "@/constants";
import { LineDataset } from "@/types/ChartData";
import { format, parseISO } from "date-fns";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import MarketInsightCard from "./MarketInsightCard.vue";
import {
  fetchForecastHistory,
  ForecastHistory,
} from "./reservoirForecastHistory";

interface ReservoirData {
  week: number;
  date: string;
  historical_min: number | null;
  historical_median: number | null;
  historical_max: number | null;
  last_year_actual: number | null;
  this_year_actual: number | null;
  predicted: number | null;
}

// gridworks stores no runs for Norway total (it blends the zones on the fly), so its history is
// rebuilt from the zones' runs
const NORWAY_ZONES = ["NO1", "NO2", "NO3", "NO4", "NO5"];
const NO_HISTORY: ForecastHistory = { currentForecastDate: null, previous: [] };

const props = withDefaults(defineProps<{ allZonesUnlocked?: boolean }>(), {
  allZonesUnlocked: false,
});

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();

// State
const selectedZone = ref("NO2");
const rawData = ref<ReservoirData[]>([]);
const forecastHistory = ref<ForecastHistory>(NO_HISTORY);
const isLoading = ref(false);
const error = ref<string | null>(null);

// Zone options with availability
const zoneOptions = computed(() => {
  const zones = [
    {
      value: "NORWAY_TOTAL",
      label: t("market_insights.norway_reservoir.zones.norway_total"),
    },
    { value: "NO1", label: "NO1" },
    { value: "NO2", label: "NO2" },
    { value: "NO3", label: "NO3" },
    { value: "NO4", label: "NO4" },
    { value: "NO5", label: "NO5" },
  ];

  return zones.map((zone) => ({
    ...zone,
    disabled: zone.value === "NO2" ? false : !props.allZonesUnlocked,
  }));
});

// Parse CSV data
const parseCSV = (csvText: string): ReservoirData[] => {
  const lines = csvText.trim().split("\n");
  const headers = lines[0].split(",").map((h) => h.trim());

  return lines
    .slice(1)
    .map((line) => {
      const values = line.split(",");
      const row: any = {};

      headers.forEach((header, index) => {
        const value = values[index]?.trim();
        if (header === "week") {
          row[header] = parseInt(value);
        } else if (header === "date") {
          row[header] = value;
        } else {
          row[header] = parseNullableNumber(value);
        }
      });

      return row as ReservoirData;
    })
    .filter((row) => row.week !== 53);
};

const parseNullableNumber = (value: string | undefined): number | null => {
  if (value === "" || value === undefined) return null;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
};

const fetchLevels = async (url: string): Promise<ReservoirData[]> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Failed to fetch data");
  }
  return parseCSV(await response.text());
};

// Fetch data for selected zone
const fetchData = async () => {
  const zone = selectedZone.value;
  const url = RESERVOIR_LEVELS_URLS[zone as keyof typeof RESERVOIR_LEVELS_URLS];
  if (!url) {
    error.value = t("market_insights.norway_reservoir.no_data");
    return;
  }

  forecastHistory.value = NO_HISTORY;
  void loadForecastHistory(zone);

  isLoading.value = true;
  error.value = null;
  try {
    const levels = await fetchLevels(url);
    if (selectedZone.value !== zone) return;
    rawData.value = levels;
  } catch (err) {
    if (selectedZone.value !== zone) return;
    error.value = t("market_insights.norway_reservoir.error");
    globalErrorHandler(err);
  } finally {
    if (selectedZone.value === zone) {
      isLoading.value = false;
    }
  }
};

// The earlier forecasts are extra context: the chart doesn't wait for them, and stays complete without them
const loadForecastHistory = async (zone: string) => {
  try {
    const history = await fetchForecastHistory(
      zone === "NORWAY_TOTAL" ? NORWAY_ZONES : [zone],
      PALETTE.forecastHistory.length
    );
    if (selectedZone.value === zone) forecastHistory.value = history;
  } catch (err) {
    console.warn("Failed to load earlier reservoir forecasts", err);
  }
};

// Format date labels (e.g., "Jan 1", "Jan 8")
const dateLabels = computed(() => {
  if (!rawData.value.length) return [];
  return rawData.value.map((d) => {
    const date = new Date(d.date);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  });
});

// Create datasets for actual and predicted values with smooth transition
const createActualPredictedDataset = (): LineDataset[] => {
  const lastActualIndex = rawData.value.findLastIndex(
    (row) => row.this_year_actual !== null
  );

  const actualDataset: LineDataset = {
    label: t("market_insights.norway_reservoir.legend.this_year"),
    data: rawData.value.map((row, index) =>
      index <= lastActualIndex ? row.this_year_actual : null
    ),
    borderColor: PALETTE.role.focal,
    backgroundColor: PALETTE.role.focal,
    tension: 0.4,
    fill: false,
    spanGaps: false,
    pointRadius: 2,
    pointHoverRadius: 5,
    borderWidth: 2,
  };

  // PERF: Predicted line starts from last actual point for visual continuity
  const predictedDataset: LineDataset = {
    label: forecastLabel(forecastHistory.value.currentForecastDate),
    data: rawData.value.map((row, index) => {
      if (index === lastActualIndex) {
        return row.this_year_actual;
      }
      if (index > lastActualIndex && row.predicted !== null) {
        return row.predicted;
      }
      return null;
    }),
    borderColor: PALETTE.role.focalSoft,
    backgroundColor: PALETTE.role.focalSoft,
    tension: 0.4,
    fill: false,
    spanGaps: false,
    pointRadius: 2,
    pointHoverRadius: 5,
    borderWidth: 2,
    borderDash: [5, 5],
  };

  return [actualDataset, predictedDataset];
};

// Falls back to a plain "Predicted" when the bucket couldn't say which run made the forecast
const forecastLabel = (forecastDate: string | null) =>
  forecastDate
    ? t("market_insights.norway_reservoir.legend.forecast_from", {
        // parseISO keeps the local day; new Date("YYYY-MM-DD") is UTC and can show the day before
        date: format(parseISO(forecastDate), "MMM d"),
      })
    : t("market_insights.norway_reservoir.legend.predicted");

// previous is newest first, so the most recent one always gets the first history colour
const createPreviousPredictionDatasets = (): LineDataset[] =>
  forecastHistory.value.previous.map(({ forecastDate, levels }, index) => {
    const color = PALETTE.forecastHistory[index];
    return {
      label: forecastLabel(forecastDate),
      data: rawData.value.map(
        (row) =>
          levels.find(
            (level) =>
              level.week === row.week &&
              level.year === parseInt(row.date.slice(0, 4))
          )?.percent ?? null
      ),
      borderColor: color,
      backgroundColor: color,
      tension: 0.4,
      fill: false,
      spanGaps: false,
      pointRadius: 2,
      pointHoverRadius: 5,
      borderWidth: 1.5,
      borderDash: [2, 3],
    };
  });

const chartData = computed(() => {
  if (!rawData.value.length) return null;

  const datasets: LineDataset[] = [];

  datasets.push({
    label: t("market_insights.norway_reservoir.legend.historical_max"),
    data: rawData.value.map((d) => d.historical_max),
    borderColor: PALETTE.role.envelopeHi,
    backgroundColor: `${PALETTE.role.envelope}1A`,
    tension: 0.4,
    fill: "+2",
    spanGaps: false,
    pointRadius: 0,
    pointHoverRadius: 4,
    borderWidth: 1.4,
    borderDash: [3, 4],
  });

  datasets.push({
    label: t("market_insights.norway_reservoir.legend.historical_median"),
    data: rawData.value.map((d) => d.historical_median),
    borderColor: PALETTE.role.envelope,
    backgroundColor: PALETTE.role.envelope,
    tension: 0.4,
    fill: false,
    spanGaps: false,
    pointRadius: 0,
    pointHoverRadius: 4,
    borderWidth: 1.4,
    borderDash: [3, 4],
  });

  datasets.push({
    label: t("market_insights.norway_reservoir.legend.historical_min"),
    data: rawData.value.map((d) => d.historical_min),
    borderColor: PALETTE.role.envelopeLo,
    backgroundColor: PALETTE.role.envelopeLo,
    tension: 0.4,
    fill: false,
    spanGaps: false,
    pointRadius: 0,
    pointHoverRadius: 4,
    borderWidth: 1.4,
    borderDash: [3, 4],
  });

  datasets.push({
    label: t("market_insights.norway_reservoir.legend.last_year"),
    data: rawData.value.map((d) => d.last_year_actual),
    borderColor: PALETTE.role.reference,
    backgroundColor: PALETTE.role.reference,
    tension: 0.4,
    fill: false,
    spanGaps: false,
    pointRadius: 2,
    pointHoverRadius: 5,
    borderWidth: 2,
  });

  const actualPredictedDatasets = createActualPredictedDataset();
  datasets.push(...actualPredictedDatasets);
  datasets.push(...createPreviousPredictionDatasets());

  return {
    labels: dateLabels.value,
    datasets: datasets,
  };
});

// Watch for zone changes
watch(selectedZone, fetchData);

watch(
  () => props.allZonesUnlocked,
  (unlocked) => {
    if (!unlocked && selectedZone.value !== "NO2") {
      selectedZone.value = "NO2";
    }
  }
);

onMounted(fetchData);
</script>

<style scoped lang="scss">
.form-label-sm {
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.chart-loading-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  border-radius: 0.5rem;
  background-color: var(--phoenix-gray-1000);
  color: var(--phoenix-gray-400);
  font-size: 0.875rem;
}
</style>
